import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector, fetchInterpreterDashboardAnalytics, fetchInterpreterDashboardAppointments, setInterpreterDashboardFilters } from "slices/interpreter/dashboardSlice";
import { LegendButton } from "components/common/TableColor";
import { interpreterLegends } from "components/common/TableColor";


const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image ">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const dispatch = useDispatch()
  const { interpreterDashboard: dashboard, last_page: totalPages, loader, total, filters, current_page } = useSelector(InterpreterDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const [activeLegend, setActiveLegend] = useState(filters?.activeLegend || 0);
  const [statusValue, setStatusValue] = React.useState(filters?.statusValue || 'all');
  const defaultValues = {
    status: 'all',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    language_id: '',
    account: '',
    department: '',
    facility: '',
    service: '',
  };
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, getValues, setError: setErrors, reset, } = useForm({
    defaultValues: filters?.formValues || defaultValues
  });
  const [filterKey, setFilterKey] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const watchedValues = watch(['start_date', 'end_date', 'language_id', 'account', 'department', 'facility', 'service']);
  const [start_date, end_date, language_id, account, department, facility, service] = watchedValues;

  const paginationParams = React.useMemo(() => ({
    start_date,
    end_date,
    language_id,
    account,
    department,
    facility,
    service,
    status: statusValue !== 'all' ? statusValue : undefined
  }), [start_date, end_date, language_id, account, department, facility, service, statusValue]);


  useEffect(() => {
    const hasFilters = !!filters;
    const hasData = !Array.isArray(dashboard?.analytics) || !!dashboard?.appointments?.length;
    const disableLoaderOnMount = hasFilters || hasData;

    dispatch(fetchInterpreterDashboardAnalytics(undefined, disableLoaderOnMount))
    if (hasFilters) {
      dispatch(fetchInterpreterDashboardAppointments(current_page || 1, { pageLength: 10, ...filters.formValues, status: filters.statusValue !== 'all' ? filters.statusValue : undefined, sortBy: "id", sortDirection: "desc" }, disableLoaderOnMount))
    } else {
      dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 10, ...defaultValues, sortBy: "id", sortDirection: "desc" }, disableLoaderOnMount))
    }
  }, [])


  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  useEffect(() => {
    if (filters) {
      reset(filters.formValues);
      setActiveLegend(filters.activeLegend || 0);
      setStatusValue(filters.statusValue || 'all');
      setSelectedLanguage(filters.selectedLanguage || null);
    }
  }, [filters, reset]);

  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [interpreterLegends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    const formValues = getValues();
    dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 10, status: value, start_date: moment(formValues.start_date)?.format('YYYY-MM-DD'), end_date: moment(formValues.end_date)?.format('YYYY-MM-DD'), sortBy: "id", sortDirection: "desc" }))
    setStatusValue(value)

    dispatch(setInterpreterDashboardFilters({
      formValues: {
        ...formValues,
        status: value
      },
      activeLegend: id,
      statusValue: value,
      selectedLanguage
    }));
  }

  //#endregion


  const onSubmit = (data) => {
    dispatch(setError({}))
    dispatch(fetchInterpreterDashboardAppointments(1, data))
    clearErrors();
    setFilterKey(prev => prev + 1);

    dispatch(setInterpreterDashboardFilters({
      formValues: data,
      activeLegend,
      statusValue,
      selectedLanguage
    }));
  };


  const handleResetFields = () => {
    setValue("start_date", "");
    setValue("end_date", "");
    setValue("language_id", "");
    setSelectedLanguage(null);
    setValue("account", "");
    setValue("department", "");
    setValue("facility", "");
    setValue("service", "");
    dispatch(fetchInterpreterDashboardAppointments(1, { sortBy: "status", sortDirection: "asc" }))
    clearErrors()
    setFilterKey(prev => prev + 1);

    dispatch(setInterpreterDashboardFilters(null));
  };


  const serviceOption = [
    { value: 'In Person', label: 'In Person' },
    { value: 'VRI', label: 'Video Remote Interpretation' },
    { value: 'OPI', label: 'OTP Pre Schedule' },
    { value: 'OPI On Demand', label: 'OPI On Demand' },
  ]

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen) => {
    const today = moment().format('YYYY-MM-DD');
    const before_fourteen_days = moment().subtract(14, 'days').format('YYYY-MM-DD');

    if (isforteen) {
      setValue("start_date", before_fourteen_days);
    } else {
      setValue("start_date", today);
    }

    setValue("end_date", today);
    if (item?.value) {
      setValue("service", item?.value)
    } else {
      setValue("service", null)
    }

    const formValues = getValues();
    const data = {
      ...formValues,
      start_date: isforteen ? before_fourteen_days : today,
      end_date: today,
      service: item?.value || "",
    };

    dispatch(fetchInterpreterDashboardAppointments(1, data));
    tableRef.current.scrollIntoView({
      behavior: "smooth",
    });
    dispatch(setError({}));
    clearErrors();

    dispatch(setInterpreterDashboardFilters({
      formValues: data,
      activeLegend: 0,
      statusValue: 'all',
      selectedLanguage
    }));
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(false);
                        handleFilter(service, false);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.count}</td>
                      <td>{service?.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(true);
                        handleFilter(service, true);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.count}</td>
                      <td>{service?.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}
        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              <div className="row-primary">
                <h2>Clients</h2>
                <Controller
                  name="account"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="account" type="text" placeholder="Client name" {...field} />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="facility" type="text" placeholder="Facility" {...field} />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={selectedLanguage}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setSelectedLanguage(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Services</h2>

                <Controller
                  name="service"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={field.value ? serviceOption?.find((opt) => field.value == opt.value) : ""}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="department" type="text" placeholder="Department" {...field} />
                    </div>
                  }
                />
              </div>
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} paginationParams={paginationParams} filterKey={filterKey} />
            </div>

          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;